// lib
import { useState, useRef, useMemo, useCallback } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'

import { CUSTOM_QUESTION_REG } from '@aytm/app/stats/page/components/lib/constants'
import { hasComments } from '@aytm/app/stats/page/components/lib/utils'

import { PRESETS } from '@aytm/app/stats/page/question-views/components/word-cloud/constants/presets'

// components
import { Button } from 'primereact/button'
import { Dropdown } from 'primereact/dropdown'

import ExportDropdown from '@aytm/app/stats/components/export-dropdown/ExportDropdown'
import { pressable } from '@aytm/app/stats/components/lib/pressable'

// styles
import style from '@aytm/app/stats/page/question-views/oe/components/styles/Controls.module.scss'

const itemTemplate = option => (option.hidden ? null : <span dangerouslySetInnerHTML={{ __html: option.text }}></span>)
const valueTemplate = (option, _props) =>
  option ? (
    <span dangerouslySetInnerHTML={{ __html: `Preset: ${option.label || option.text}` }}></span>
  ) : (
    <span dangerouslySetInnerHTML={{ __html: _props.placeholder || 'Select...' }}></span>
  )

const WordCloudControls = ({
  model,
  data,
  config,
  features,
  applyToAllLabel,
  collapsed,
  isVerbatimType,
  isSurveyGuest,
  onPresetChange,
  onApplyToAll,
  onExport,
  onToggle
}) => {
  const { id, type, items } = model
  const { customPresets } = data
  const { currentPresetId } = config

  const [status, setStatus] = useState(null)

  const timer = useRef(null)

  const statusRef = useRef(null)
  statusRef.current = status

  const presetsOptions = useMemo(
    () =>
      PRESETS.map(item => {
        return { key: item[2], text: item[0] }
      }).concat(
        customPresets.map((_item, index) => {
          return { key: `custom-${index + 1}`, text: `Most recent custom #${index + 1}` }
        })
      ),
    [customPresets]
  )

  const exportOptions = useMemo(() => {
    const options = [
      { key: 'png', text: 'Download PNG image' },
      { key: 'pdf', text: 'Download PDF document' },
      { key: 'eps', text: 'Download EPS file' }
    ]

    window.Blob != undefined && options.push({ key: 'csv', text: 'Download CSV file' })

    const allowed = ['oe', 'oex', 'cn'].includes(type) || null === items || hasComments(items)
    if (allowed && isVerbatimType && !features.oe_tags_management.disabled) {
      options.push({ key: 'csv-verbatim', text: 'Download all verbatim (CSV)' })
      if (!CUSTOM_QUESTION_REG.test(String(id).split('-')[0])) {
        options.push({ key: 'csv-verbatim-codes', text: 'Download all verbatim with codes (CSV) ' })
      }
    }

    return options
  }, [id, type, items, isVerbatimType, features.oe_tags_management.disabled])

  const onToggleClick = useCallback(() => {
    setStatus('toggling')
    'function' == typeof onToggle && onToggle()

    timer.current && clearTimeout(timer.current)
    timer.current = setTimeout(() => 'toggling' == statusRef.current && setStatus(null), 500)

    return () => timer.current && clearTimeout(timer.current)
  }, [onToggle])

  return (
    <>
      {features.word_clouds.disabled ? null : (
        <>
          <div className="p-fluid p-col-param p-col-param-preset">
            <Dropdown
              className="p-dropdown-rounded"
              aria-label="Word cloud preset"
              ariaLabel="Word cloud preset"
              placeholder="Preset: Custom..."
              value={currentPresetId}
              options={presetsOptions}
              optionValue="key"
              optionLabel="text"
              optionDisabled="disabled"
              fixedDropdownDir="bottom"
              itemTemplate={itemTemplate}
              valueTemplate={valueTemplate}
              onChange={event => onPresetChange(event.value)}
            />
          </div>
          {isSurveyGuest ? null : (
            <div className="p-col-button">
              <Button
                className="p-button-grey p-button-rounded"
                label={applyToAllLabel || 'Apply to all wordclouds'}
                onClick={onApplyToAll}
              />
            </div>
          )}
        </>
      )}
      <div className="p-right">
        {features.word_clouds.disabled ? null : (
          <div className="p-field">
            <span
              className={style.toggle}
              aria-label="Word cloud settings"
              aria-expanded={!collapsed}
              {...pressable(onToggleClick)}
            >
              <i
                className={classNames('icon-aytmfd-config', {
                  ['aytmfd-spin']: 'toggling' == status && !collapsed,
                  ['aytmfd-spin-reverse']: 'toggling' == status && collapsed
                })}
              />
            </span>
          </div>
        )}
        {!isSurveyGuest ? <ExportDropdown options={exportOptions} onExport={onExport} /> : null}
      </div>
    </>
  )
}

WordCloudControls.propTypes = {
  model: PropTypes.object,
  data: PropTypes.object,
  features: PropTypes.object,
  applyToAllLabel: PropTypes.string,
  collapsed: PropTypes.bool,
  isVerbatimType: PropTypes.bool,
  isSurveyGuest: PropTypes.bool,
  onPresetChange: PropTypes.func,
  onApplyToAll: PropTypes.func,
  onExport: PropTypes.func,
  onToggle: PropTypes.func
}

WordCloudControls.displayName = 'WordCloudControls'

export default WordCloudControls
